import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { Session, User, AuthResponse } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export type ProfileRole = 'owner' | 'produccion' | 'direccion' | 'guest' | 'desarrollo';

export interface Profile {
  user_id: string;
  email: string | null;
  name: string | null;
  role: ProfileRole;
}

interface AuthState {
  loading: boolean;
  session: Session | null;
  user: User | null;
  profile: Profile | null;
}

interface SignUpConsents {
  version: string;
  termsPrivacy: boolean;
  consumerImmediateStart: boolean;
  marketing: boolean;
}

interface AuthContextValue extends AuthState {
  isAuthenticated: boolean;
  isOwner: boolean;
  isStaff: boolean;
  signOut: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  signUp: (email: string, password: string, consents: SignUpConsents) => Promise<AuthResponse['data']>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    loading: true,
    session: null,
    user: null,
    profile: null,
  });

  const fetchProfile = useCallback(async (userId: string): Promise<Profile | null> => {
    const { data, error } = await supabase
      .from('profiles')
      .select('user_id, email, name, role')
      .eq('user_id', userId)
      .maybeSingle();
    if (error || !data) return null;
    return data as Profile;
  }, []);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return;
      if (!session) {
        setState({ loading: false, session: null, user: null, profile: null });
        return;
      }
      const profile = await fetchProfile(session.user.id);
      if (cancelled) return;
      setState({ loading: false, session, user: session.user, profile });
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        setState({ loading: false, session: null, user: null, profile: null });
        return;
      }
      setTimeout(() => {
        if (cancelled) return;
        fetchProfile(session.user.id).then((profile) => {
          if (cancelled) return;
          setState({ loading: false, session, user: session.user, profile });
        });
      }, 0);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const sendMagicLink = useCallback(async (email: string) => {
    const redirectTo = `${window.location.origin}/auth/callback`;
    // INVITE-ONLY: shouldCreateUser:false impide que el magic link cree cuentas
    // nuevas desde la web. Solo entran usuarios ya existentes (que tú invitas
    // desde Supabase Dashboard > Authentication > Users). Para el blindaje
    // server-side total, además poner Authentication > Sign In/Up: "Allow new
    // users to sign up" = OFF (disable_signup) en el Dashboard.
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo, shouldCreateUser: false },
    });
    if (error) throw error;
  }, []);

  const signUp = useCallback(async (email: string, password: string, consents: SignUpConsents) => {
    const redirectTo = `${window.location.origin}/auth/callback`;
    const now = new Date().toISOString();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectTo,
        data: {
          consent_version: consents.version,
          consent_terms_privacy: consents.termsPrivacy,
          consent_consumer_immediate_start: consents.consumerImmediateStart,
          consent_marketing: consents.marketing,
          consent_at: now,
          terms_accepted: true,
          terms_version: consents.version,
          terms_accepted_at: now,
        },
      },
    });
    if (error) throw error;
    return data;
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const redirectTo = `${window.location.origin}/auth/actualizar-clave`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  }, []);

  const value: AuthContextValue = {
    ...state,
    isAuthenticated: !!state.session,
    isOwner: state.profile?.role === 'owner',
    isStaff: state.profile?.role === 'owner' || state.profile?.role === 'desarrollo',
    signOut,
    sendMagicLink,
    signUp,
    signInWithPassword,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
